import { useCallback, useEffect, useRef, useState } from "react";
import { Input, Modal, Select, Switch, message } from "antd";
import { FileText, Sparkles, Upload, X } from "lucide-react";

import { useExtractPlanReviewRequirementsMutation } from "graphql/mutations/extract-plan-review-requirements.generated";
import { useInsertJurisdictionResourcesMutation } from "graphql/mutations/insert-jurisdiction-resources.generated";

import useFileDropzone from "hooks/useFileDropzone";
import { useFileUpload } from "hooks/useFileUpload";
import { UploadScope } from "state/reactive-vars/uploading-files-var";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { PLAN_REVIEW_GUIDELINE_ACCEPTED_TYPES, PLAN_REVIEW_GUIDELINE_MAX_FILE_SIZE_BYTES } from "constants/constants";

interface SubmissionTypeOption {
	id: string;
	name: string;
}

interface PlanReviewExtractModalProps {
	open: boolean;
	jurisdictionId: string;
	submissionTypes: SubmissionTypeOption[];
	onCancel: () => void;
	onStarted: (extractionId: string) => void;
}

interface StagedGuideline {
	// The id the upload slot filed it under, so removing it here also frees the slot.
	localId: string;
	name: string;
	url: string;
	mimeType: string;
	size: number;
}

const formatBytes = (bytes: number): string => {
	if (!bytes) return "";
	const units = ["B", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(1024));
	return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

/**
 * Step 1 of the AI extraction: what Ollie should read.
 *
 * Nothing is written to the rules container here — the run only produces candidates,
 * which the reviewer approves on the next screen.
 */
const PlanReviewExtractModal = ({
	open,
	jurisdictionId,
	submissionTypes,
	onCancel,
	onStarted
}: PlanReviewExtractModalProps) => {
	const { loggedUserValue } = useLoggedUser();
	const { uploadFiles, removeFile, clearAllFiles } = useFileUpload(UploadScope.PLAN_REVIEW_GUIDELINES);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const [documents, setDocuments] = useState<StagedGuideline[]>([]);
	const [instructions, setInstructions] = useState("");
	const [submissionTypeIds, setSubmissionTypeIds] = useState<string[]>([]);
	const [addToResources, setAddToResources] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [isStarting, setIsStarting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Every run starts from an empty form, the upload slot included. What a set of rules
	// was generated from has to be a deliberate statement about this run, not the leftovers
	// of the last one — and a slot still holding the previous run's documents turns the
	// same file away as "already attached" when it is uploaded again here.
	useEffect(() => {
		if (!open) return;
		setDocuments([]);
		setInstructions("");
		setSubmissionTypeIds([]);
		setAddToResources(false);
		setError(null);
		clearAllFiles();
	}, [open]);

	const [extractRequirements] = useExtractPlanReviewRequirementsMutation();
	const [insertResources] = useInsertJurisdictionResourcesMutation();

	const handleFilesSelected = useCallback(
		async (fileList: FileList | null) => {
			const files = Array.from(fileList ?? []);
			if (files.length === 0) return;

			// Each file is read whole by the model, so the ceiling is per file.
			const oversized = files.filter(f => f.size > PLAN_REVIEW_GUIDELINE_MAX_FILE_SIZE_BYTES);
			oversized.forEach(f =>
				message.error(
					`${f.name} is ${formatBytes(f.size)}. Guidelines must be under ${formatBytes(
						PLAN_REVIEW_GUIDELINE_MAX_FILE_SIZE_BYTES
					)} — please split it and attach the parts.`
				)
			);
			const accepted = files.filter(f => f.size <= PLAN_REVIEW_GUIDELINE_MAX_FILE_SIZE_BYTES);
			if (accepted.length === 0) {
				if (fileInputRef.current) fileInputRef.current.value = "";
				return;
			}

			const sizeByName = new Map(accepted.map(f => [f.name, f.size]));
			setIsUploading(true);
			try {
				const results = await uploadFiles(accepted);
				const uploaded = results
					.filter((r): r is NonNullable<typeof r> => !!r?.publicUrl)
					.map(r => {
						const name = r.fileName ?? "document";
						return {
							localId: r.id,
							name,
							url: r.publicUrl as string,
							mimeType: r.mimeType ?? "application/pdf",
							size: sizeByName.get(name) ?? 0
						};
					});
				// Re-picking the same file replaces it instead of attaching it twice.
				setDocuments(prev => [...prev.filter(d => !uploaded.some(u => u.name === d.name)), ...uploaded]);
			} catch (uploadError) {
				console.error("Guideline upload failed:", uploadError);
				message.error("Some documents couldn't be uploaded. Please try again.");
			} finally {
				setIsUploading(false);
				if (fileInputRef.current) fileInputRef.current.value = "";
			}
		},
		[uploadFiles]
	);

	// Also freed from the upload slot: a document taken off the list has to be attachable
	// again, and the slot is what the duplicate check reads.
	const handleRemove = useCallback(
		(doc: StagedGuideline) => {
			setDocuments(prev => prev.filter(d => d.localId !== doc.localId));
			removeFile(doc.localId);
		},
		[removeFile]
	);

	const { isDraggingOver, dropzoneProps } = useFileDropzone(handleFilesSelected, isUploading);

	const canStart = (documents.length > 0 || !!instructions.trim()) && !isUploading && !isStarting;

	const handleStart = useCallback(async () => {
		if (!canStart) return;
		setIsStarting(true);
		setError(null);
		try {
			const result = await extractRequirements({
				variables: {
					input: {
						jurisdiction_id: jurisdictionId,
						workspace_id: loggedUserValue?.workspace_id,
						created_by_user_id: loggedUserValue?.id,
						instructions: instructions.trim() || null,
						submission_type_ids: submissionTypeIds,
						documents: documents.map(d => ({
							file_name: d.name,
							file_url: d.url,
							mime_type: d.mimeType,
							file_size_bytes: d.size
						}))
					}
				}
			});

			const output = result.data?.extract_plan_review_requirements;
			if (!output?.success || !output.extraction_id) {
				throw new Error(output?.error ?? "The extraction couldn't be started.");
			}

			// The run is already going, so a resource that can't be saved must not hold it back.
			if (addToResources && documents.length > 0) {
				try {
					await insertResources({
						variables: {
							objects: documents.map(d => ({
								jurisdiction_id: jurisdictionId,
								name: d.name.replace(/\.[^.]+$/, ""),
								file_name: d.name,
								file_url: d.url,
								mime_type: d.mimeType,
								file_size_bytes: d.size,
								created_by_user_id: loggedUserValue?.id
							}))
						}
					});
				} catch (resourceError) {
					console.error("Adding the extraction files to Resources failed:", resourceError);
					message.warning("The extraction started, but the files couldn't be added to your Resources.");
				}
			}

			onStarted(output.extraction_id);
		} catch (startError) {
			console.error("Rule extraction failed to start:", startError);
			setError(startError instanceof Error ? startError.message : "The extraction couldn't be started.");
		} finally {
			setIsStarting(false);
		}
	}, [
		canStart,
		extractRequirements,
		jurisdictionId,
		loggedUserValue,
		instructions,
		submissionTypeIds,
		documents,
		addToResources,
		insertResources,
		onStarted
	]);

	return (
		<Modal open={open} onCancel={onCancel} footer={null} closable={false} width={720} className="prem-modal">
			<h2 className="prem-modal-title">Generate rules with AI</h2>
			<p className="prem-modal-sub">
				Ollie reads your submittal guidelines and proposes rules. Nothing is saved until you review them.
			</p>

			<div className="prem-form">
				<div className="prem-form-field prem-form-field-full">
					<label className="prem-label">Guideline documents</label>
					{documents.map(doc => (
						<div key={doc.localId} className="prem-file-row">
							<div className="prem-file-info">
								<FileText size={18} color="#4f39f6" />
								<span className="prem-file-name">{doc.name}</span>
								<span className="prem-file-size">{formatBytes(doc.size)}</span>
							</div>
							<button type="button" className="prem-icon-btn" title="Remove" onClick={() => handleRemove(doc)}>
								<X size={15} />
							</button>
						</div>
					))}
					<label className={`prem-dropzone ${isDraggingOver ? "prem-dropzone-active" : ""}`} {...dropzoneProps}>
						<Upload size={20} />
						<span>
							{isUploading
								? "Uploading…"
								: isDraggingOver
									? "Drop the documents here"
									: documents.length > 0
										? "Add another document"
										: "Drag and drop or tap to upload your submittal guidelines"}
						</span>
						<span className="prem-file-size">
							Attach every PDF that carries rules — guidelines, appendices, your amended code. Optional if you write
							instructions below.
						</span>
						<input
							ref={fileInputRef}
							type="file"
							accept={PLAN_REVIEW_GUIDELINE_ACCEPTED_TYPES}
							multiple
							className="prem-dropzone-input"
							onChange={e => handleFilesSelected(e.target.files)}
						/>
					</label>
				</div>

				<div className="prem-form-field prem-form-field-full">
					<label className="prem-label">Instructions for the AI</label>
					<Input.TextArea
						className="prem-control prem-textarea"
						rows={4}
						placeholder={
							"Anything the documents do not say, or say differently. For example:\n• We follow IFC 2021 as amended locally.\n• Warehouses over 200,000 sq ft need two extra extinguishers per storage aisle.\n• Ignore the fee schedule in section 4."
						}
						value={instructions}
						onChange={e => setInstructions(e.target.value)}
					/>
					<p className="prem-hint">
						These override the document where they disagree, and any requirement they state becomes a rule. They are
						saved with this run, so you can always see what a set of rules was generated from.
					</p>
				</div>

				<div className="prem-form-field prem-form-field-full">
					<label className="prem-label">Apply to submission types</label>
					<Select
						className="prem-control"
						mode="multiple"
						placeholder="Which submittals do these rules apply to?"
						value={submissionTypeIds}
						options={submissionTypes.map(t => ({ label: t.name, value: t.id }))}
						onChange={setSubmissionTypeIds}
					/>
					<p className="prem-hint">The starting point for every proposed rule; you can change it per rule.</p>
				</div>
			</div>

			{documents.length > 0 ? (
				<div className="prem-resources-toggle">
					<Switch checked={addToResources} onChange={setAddToResources} className="prem-switch" />
					<span className="prem-hint">Add this file to your Resources so that Ollie can reference in customer chats?</span>
				</div>
			) : null}

			{error ? <p className="prem-error">{error}</p> : null}

			<div className="prem-modal-actions">
				<button type="button" className="prem-btn" onClick={onCancel} disabled={isStarting}>
					Cancel
				</button>
				<button type="button" className="prem-btn prem-btn-primary" onClick={handleStart} disabled={!canStart}>
					<Sparkles size={16} />
					{isStarting ? "Starting…" : "Extract rules"}
				</button>
			</div>
		</Modal>
	);
};

export default PlanReviewExtractModal;
